import { type MouseEvent, useEffect, useMemo, useRef, useState } from "react";
import type { DropdownProps } from "@/components/mosaic/ui/TileTitle/TileTitle";
import { Button } from "@/components/ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandItem,
	CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { ToastAction } from "@/components/ui/toast";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/components/ui/use-toast";
import { MonitorStateService } from "@/components/widgets/Monitor/lib/services/MonitorStateService";
import { ScreenerStateService } from "@/components/widgets/Screener/services/ScreenerStateService";
import { useAuth } from "@/hooks/useAuth";
import { useAuthService } from "@/hooks/useAuthService";
import { useEventSubscription } from "@/hooks/useEventSubscription";
import { useGetUserDataFromToken } from "@/hooks/useGetUserDataFromToken";
import { cn, compareStrings } from "@/lib/utils";
import { HoverCardWrapper } from "@/shared/components/HoverCardWrapper";
import { OfferToUpgrade } from "@/shared/components/OfferToUpgrade";
import { buttonClassNames } from "@/shared/constants/customStyleSets";
import { KEY } from "@/shared/constants/keyboard";
import { EVENTS } from "@/shared/eventEmiters/consts";
import { getDefaultName } from "@/shared/helpers/getDefaultName";
import sprites from "/src/shared/assets/sprite.svg";
import { useScreenerState } from "../../hooks/useScreenerState";
import { useCanUpdateOrSave } from "../../hooks/useScreenerState/useCanUpdateOrSave";
import type { IStateManagement } from "../../types";

export const StateManagement = ({
	eventEmitter,
	widgetName,
	state: widgetState,
	onStateChange,
}: IStateManagement & DropdownProps) => {
	const { login } = useAuth();
	const userData = useGetUserDataFromToken();
	const userId = userData?.username || "";
	const inputRef = useRef<HTMLInputElement>(null);

	const [openPopover, setOpenPopover] = useState(false);
	const [widgetId, setWidgetId] = useState("");
	const { toast } = useToast(widgetId);
	const authService = useAuthService();

	const service = useMemo(() => {
		if (widgetName === "Screener") {
			return ScreenerStateService.create();
		}
		return MonitorStateService.create();
	}, [widgetName]);

	const {
		states,
		state,
		name,
		setName,
		setState,
		getStates,
		getState,
		removeState,
		addState,
		resetState,
	} = useScreenerState(
		eventEmitter,
		widgetName,
		widgetId,
		service,
		widgetState,
	);

	const statesByPlan = states.map((stateItem, index) => ({
		...stateItem,
		disabled: index > authService.savedStateLimit - 1,
	}));

	const { canUpdate, canSave } = useCanUpdateOrSave({
		states,
		name,
	});

	useEffect(() => {
		const fetchDefaultName = async () => {
			const newName = await getDefaultName(
				widgetName,
				userId,
				undefined, // avoid setting name in the function
				service,
			);
			setName((prev) => prev || newName);
		};

		if (userId) {
			fetchDefaultName();
		}
	}, [widgetName, userId, setName, service]);

	useEventSubscription(eventEmitter, EVENTS.WIDGET_PART_STATE, (data: any) => {
		setState(data);
		onStateChange(data, false);
	});
	useEventSubscription(eventEmitter, EVENTS.ID, setWidgetId);

	const handleLogin = (e: MouseEvent) => {
		e.preventDefault();
		login();
	};

	const handleSaveOrUpdate = () => {
		if (canUpdate || canSave) {
			addState();
		}
	};

	return (
		<>
			<Popover open={openPopover} onOpenChange={setOpenPopover}>
				<PopoverTrigger asChild>
					<Input
						className="bg-background w-[106px] h-[18px]"
						type="text"
						placeholder={`Name of ${widgetName.toLocaleLowerCase()}...`}
						value={name}
						onClick={getStates}
						onChange={(e) => setName(e.target.value)}
						onFocus={() => {
							inputRef.current?.focus();
							inputRef.current?.select();
						}}
					/>
				</PopoverTrigger>

				<PopoverContent
					className="w-[280px] p-0"
					onOpenAutoFocus={(e) => {
						e.preventDefault();
					}}
					onCloseAutoFocus={(e) => {
						e.preventDefault();
						inputRef.current?.focus();
						inputRef.current?.select();
					}}
				>
					<Command>
						{statesByPlan.length !== 0 ? (
							<CommandGroup data-testid="list-of-states">
								<CommandList>
									{statesByPlan.map((stateItem) => {
										return (
											<HoverCardWrapper
												key={stateItem.itemName}
												data-testid="add-tab-button"
												trigger={
													<CommandItem
														key={stateItem.itemName}
														className={cn(
															"cursor-pointer flex justify-between hover:text-gold rounded-sm text-base m-0 px-1",
															{
																"text-gold":
																	compareStrings(
																		state?.itemName,
																		stateItem.itemName,
																	) === 0,
																"bg-(--dark-background)":
																	compareStrings(
																		state?.itemName,
																		stateItem.itemName,
																	) === 0,
															},
														)}
														onSelect={() => {
															getState(stateItem.itemName);
															setOpenPopover(false);
														}}
														onKeyDown={(event) => {
															if (
																event.key === KEY.ENTER ||
																event.key === KEY.SPACE
															) {
																event.preventDefault();
																getState(stateItem.itemName);
															}
														}}
														disabled={stateItem.disabled}
													>
														<p className="whitespace-normal break-all">
															{stateItem.itemName}
														</p>

														<div
															className="h-max bg-transparent p-0"
															onClick={(event) => {
																event.stopPropagation();
																toast({
																	title: `Delete ${stateItem.itemName}`,
																	action: (
																		<ToastAction
																			altText="Confirm remove state"
																			onClick={() =>
																				removeState(event, stateItem.itemName)
																			}
																		>
																			Confirm
																		</ToastAction>
																	),
																	duration: 5000,
																	className: "py-2! pr-[28px] gap-1",
																});
															}}
															onKeyDown={() => {}}
														>
															<svg
																aria-label="close"
																role="img"
																fill="none"
																stroke="var(--light-gray)"
																className="w-3 h-3 group-hover:stroke-accent-foreground ml-1"
															>
																<use xlinkHref={`${sprites}#close`} />
															</svg>
														</div>
													</CommandItem>
												}
												content={
													<OfferToUpgrade
														offer={
															<p className="text-center">Upgrade to unlock</p>
														}
													/>
												}
												disabled={stateItem.disabled}
											/>
										);
									})}
								</CommandList>
							</CommandGroup>
						) : (
							<CommandEmpty
								className="py-6 px-2 text-center text-lg"
								data-testid="no-states"
							>
								{userId ? (
									`No ${widgetName.toLocaleLowerCase()}s saved`
								) : (
									<div className="flex flex-col items-center gap-2">
										<p>{`To build and save multiple ${widgetName || name}s, please upgrade to  ${authService.color === "silver" ? " a Gold plan" : "Silver (3) or Gold (multiple) plan"}`}</p>

										<Button className={buttonClassNames} onClick={handleLogin}>
											Upgrade
										</Button>
									</div>
								)}
							</CommandEmpty>
						)}
					</Command>
				</PopoverContent>
			</Popover>

			<HoverCardWrapper
				trigger={
					<div
						className="border-none px-0 bg-transparent cursor-pointer"
						onClick={handleSaveOrUpdate}
						data-testid="add-state-button"
						onKeyDown={(e) => e.preventDefault()}
					>
						<Tooltip>
							<TooltipTrigger asChild>
								<svg
									aria-label="save-state"
									role="img"
									stroke="#fff"
									className="w-4 h-4"
								>
									<use xlinkHref={`${sprites}#save-state`} />
								</svg>
							</TooltipTrigger>

							<TooltipContent>
								{canUpdate ? "Update" : "Save"} {widgetName}
							</TooltipContent>
						</Tooltip>
					</div>
				}
				content={
					<OfferToUpgrade
						offer={
							<p>{`To build and save multiple ${widgetName || name}s, please upgrade to  ${authService.color === "silver" ? " a Gold plan" : "Silver (3) or Gold (multiple) plan"}`}</p>
						}
						buttonText="Upgrade"
					/>
				}
				disabled={!canUpdate && !canSave}
			/>

			<Button
				disabled={!userId}
				variant="outline"
				className="border-none px-0 bg-transparent h-[18px] hover:bg-transparent"
				onClick={resetState}
				data-testid="reset-state-button"
			>
				<Tooltip>
					<TooltipTrigger asChild disabled={!userId}>
						<svg aria-label="plus" role="img" stroke="#fff" className="w-4 h-4">
							<use xlinkHref={`${sprites}#plus`} />
						</svg>
					</TooltipTrigger>
					<TooltipContent>Add a new {widgetName}</TooltipContent>
				</Tooltip>
			</Button>
		</>
	);
};
